import { Avatar, Button, theme, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { MdOutlineDone, MdOutlineSettings } from 'react-icons/md'

import { toneColors } from '@/Ship/Components/History'
import { DATETIME_FORMAT } from '@/Ship/Configs/DateFormat'
import { classNames } from '@/Ship/Helpers/classNames'
import { initials } from '@/Ship/Helpers/fieldViews'

import { notificationEntity, notificationKind } from '../Helpers/notificationView'

import type { Notification } from '../Stores/NotificationStore'
import type { KeyboardEvent, MouseEvent } from 'react'

import styles from './Notifications.module.css'

interface NotificationListItemProps {
    item: Notification
    // eslint-disable-next-line no-unused-vars -- ім'я параметра в типі функції
    onOpen: (item: Notification) => void
    // eslint-disable-next-line no-unused-vars -- ім'я параметра в типі функції
    onMarkRead: (item: Notification) => void
}

/**
 * Одне сповіщення: що сталося, з яким записом, хто і коли.
 *
 * Рядок цілком — посилання на запис: клік відкриває картку й позначає
 * сповіщення прочитаним. Позначити, не відкриваючи, — галочкою на місці
 * синьої крапки, вона з'являється під курсором.
 */
export const NotificationListItem = ({ item, onOpen, onMarkRead }: NotificationListItemProps) => {
    const { token } = theme.useToken()
    const kind = notificationKind(item)
    const entity = notificationEntity(item)
    const tone = toneColors(token, kind.tone)
    const time = item.createdAt ? dayjs(item.createdAt) : null

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onOpen(item)
        }
    }

    const markRead = (event: MouseEvent) => {
        event.stopPropagation()
        onMarkRead(item)
    }

    return (
        <div
            role="button"
            tabIndex={0}
            className={classNames(styles['item'], !item.isReaded && styles['unread'])}
            onClick={() => onOpen(item)}
            onKeyDown={onKeyDown}
        >
            <span className={classNames(styles['icon'])} style={{ background: tone.bg, color: tone.fg }}>
                {kind.icon}
            </span>

            <div className={classNames(styles['content'])}>
                <div className={classNames(styles['title'])}>{kind.label}</div>

                {entity ? (
                    <span className={classNames(styles['link'])} title={entity.label}>
                        <span className={classNames(styles['linkLabel'])}>{entity.label}</span>
                    </span>
                ) : (
                    <span className={classNames(styles['link'])}>{item.message}</span>
                )}

                <div className={classNames(styles['author'])}>
                    {item.authorName ? (
                        <>
                            <Avatar
                                size={20}
                                style={{ background: token.colorPrimaryBg, color: token.colorPrimary, fontSize: 9, flexShrink: 0 }}
                            >
                                {initials(item.authorName)}
                            </Avatar>
                            {item.authorName}
                        </>
                    ) : (
                        <>
                            <Avatar
                                size={20}
                                icon={<MdOutlineSettings />}
                                style={{ background: token.colorFillTertiary, color: token.colorTextSecondary, fontSize: 11, flexShrink: 0 }}
                            />
                            Система
                        </>
                    )}
                </div>
            </div>

            <div className={classNames(styles['meta'])}>
                {time?.isValid() && (
                    <span className={classNames(styles['time'])} title={time.format(DATETIME_FORMAT)}>
                        {time.format('HH:mm')}
                    </span>
                )}

                {!item.isReaded && (
                    <span className={classNames(styles['marker'])}>
                        <span className={classNames(styles['dot'])} />
                        <Tooltip title="Позначити прочитаним" placement="left">
                            <Button
                                type="text"
                                size="small"
                                shape="circle"
                                aria-label="Позначити прочитаним"
                                className={classNames(styles['markRead'])}
                                icon={<MdOutlineDone style={{ fontSize: 16 }} />}
                                onClick={markRead}
                            />
                        </Tooltip>
                    </span>
                )}
            </div>
        </div>
    )
}
